/* eslint-disable-next-line import/no-unresolved */
import { useCallback, useEffect, useMemo, useState } from 'react';
/* eslint-disable-next-line import/no-unresolved */
import { useDispatch, useSelector } from 'react-redux';
import {
  AP_GROUP_STATUSES,
  CARD_STATUS_IDS,
  CFD,
  DEFAULT_LAB_SELECTED_TERM_ID,
  ETF,
  FX,
  TERM_ID_FROM_START_DATE,
} from '../constants';
import { useSelectedShareItem, useSelectedStrategyGroup } from '.';
import { useCalculatingChartDataWithLabels } from '../services/hooks';
import { useLabTableData, useSelectionsTableData } from '../services/hooks/autoSelectDetails';
import { publishedLabSelector, publishedLabSimulationSelector, publishedLabsSelector } from '../redux/labs';
import {
  addToCartRequest,
  createSelectionApGroupRequest,
  saveAutoSelectFilterCondition,
} from '../redux/actions/autoSelectActions';
import { getStockLeft, normalizeLabStrategy } from '../services/select';
import { useSimulationSummary } from './useSimulationSummary';
import { getApGroupRequest } from '../redux/actions/portfolioActions';
import { getTechnicalBuilderDataRequest } from '../redux/tech';
import {
  ID_RANGE_OUTS_EXCLUDE_RANGE_OUT,
  KEY_RANGE_OUTS,
  LOGIC_ID_TERM_OPTIONS_MAP,
  TREND_STRATEGY_LOGIC_ID,
} from '../constants/select';
import { getServiceId } from '../utils';
import { saveCreatingStrategiesInfo } from '../redux/actions/builderActions';

export const useLoadingSelections = () => {
  const { areActiveSelectionsLoading, areSelectionTagsLoading } = useSelector((state) => state.autoSelect);
  const { loading } = useSelector(publishedLabsSelector);
  return useMemo(() => {
    // mobile 都合で areSelectionTagsLoading は必ず含まれなければならないことに注意
    return loading || areActiveSelectionsLoading || areSelectionTagsLoading;
  }, [areActiveSelectionsLoading, areSelectionTagsLoading, loading]);
};

export const useTableLoading = () => {
  const isLoadingModal = useSelector((state) => state.autoSelect.selectionModalInfoIsLoading);
  const isLoaderTermId = useSelector((state) => state.autoSelect.selectionModalTermInfoIsLoading);
  return useMemo(() => isLoadingModal || isLoaderTermId, [isLoadingModal, isLoaderTermId]);
};

const useSelectedDataSelection = () => {
  const defaultSelectionTermId = useSelector((state) => state.constants.defaultSelectionTermId);
  const selectedTermId = useSelector((state) => state.autoSelect.selectedTermId);
  const { [defaultSelectionTermId]: defaultTermIdInfo, [selectedTermId]: selectTermIdInfo } = useSelector(
    (state) => state.autoSelect.selectionModalInfo,
  );

  const info = selectTermIdInfo || defaultTermIdInfo || {};
  const { selectionId, selectionVersion, defaultSets, simulationStats, strategyList, serviceIds, attribute } = info;

  const loadingModal = useSelector((state) => state.autoSelect.selectionModalInfoIsLoading);
  const loadingTermId = useSelector((state) => state.autoSelect.selectionModalTermInfoIsLoading);

  return {
    selectionId,
    selectionVersion,
    defaultSets,
    simulationStats,
    strategyList,
    serviceIds,
    loading: loadingModal || loadingTermId,
    attribute: attribute ?? {},
  };
};

const useSelectedDataLab = () => {
  const publishedLab = useSelector(publishedLabSelector);
  const { labId, defaultSets = 1, serviceId, serviceIds } = publishedLab || {};
  const { result: data, loading } = useSelector(publishedLabSimulationSelector);
  const { simulationStats, strategyList, attribute } = data ?? {};

  return {
    labId,
    defaultSets,
    data,
    simulationStats,
    attribute: attribute ?? {},
    strategyList,
    serviceId,
    serviceIds,
    loading,
  };
};

export const useOrderSettingTableDataLab = () => {
  const { serviceId, defaultSets = 1, strategyList, loading } = useSelectedDataLab();
  const { autoTradeSettingsData, helpers } = useLabTableData(strategyList ?? [], defaultSets, serviceId);
  return { autoTradeSettingsData, helpers, loading };
};

const useExecutionTableDataLabInternal = ({ serviceId, defaultSets, strategyList }) => {
  const { simulationTradesData, autoTradeSettingsData, helpers } = useLabTableData(
    strategyList ?? [],
    defaultSets,
    serviceId,
  );
  return { simulationTradesData, autoTradeSettingsData, helpers };
};

export const useExecutionTableDataLab = () => {
  const selectedData = useSelectedDataLab();
  const tableData = useExecutionTableDataLabInternal(selectedData);
  return { ...tableData, loading: selectedData.loading };
};

export const useSimulationChartDataSelection = (countValue) => {
  const { selectionId, selectionVersion, strategyList, serviceIds, loading, simulationStats, defaultSets, attribute } =
    useSelectedDataSelection();
  const {
    chartData,
    chartRoi,
    totalPl,
    chartRiskReturn,
    roundedMarginRecommended,
    roundedMarginRecommendedFX,
    roundedMarginRecommendedETF,
    roundedMarginRecommendedCFD,
    marginRequired,
  } = useCalculatingChartDataWithLabels({
    strategyList,
    defaultSets: countValue ?? defaultSets,
  });
  const loadingChart = useMemo(() => ((chartData?.length ?? 0) > 0 ? false : loading), [loading, chartData]);
  const simulationData = useMemo(() => {
    return {
      roi: chartRoi?.value,
      totalPl: totalPl?.value,
      riskReturn: chartRiskReturn?.value,
      marginRecommended: roundedMarginRecommended?.value,
      marginRecommendedFX: roundedMarginRecommendedFX?.value,
      marginRecommendedETF: roundedMarginRecommendedETF?.value,
      marginRecommendedCFD: roundedMarginRecommendedCFD?.value,
      marginRequired: marginRequired?.value,
      count:
        strategyList?.reduce((total, strategy) => {
          return total + (strategy.itemList?.length ?? 0);
        }, 0) ?? 0,
      tradeCount: simulationStats?.tradeCount,
      defaultSets,
      attribute,
    };
  }, [
    chartRoi,
    totalPl,
    chartRiskReturn,
    roundedMarginRecommended,
    roundedMarginRecommendedFX,
    roundedMarginRecommendedETF,
    roundedMarginRecommendedCFD,
    marginRequired,
    strategyList,
    simulationStats,
    defaultSets,
    attribute,
  ]);
  return {
    chartData,
    simulationData,
    serviceId: serviceIds,
    selectionId,
    selectionVersion,
    loading: loadingChart,
    strategyList,
    defaultSets,
  };
};

const useSimulationChartDataLabInternal = ({ simulationStats, strategyList, defaultSets, serviceId, attribute }) => {
  const labStrategyList = (strategyList ?? []).map((strategy) => ({
    strategyDetail: { ...strategy, serviceId },
    strategySets: strategy.strategySets,
  }));

  const { chartData, chartRoi, marginRequired, roundedMarginRecommended } = useCalculatingChartDataWithLabels({
    strategyList: labStrategyList,
    defaultSets,
  });

  const simulationData = useMemo(() => {
    const { realizedPl, unrealizedPl, riskReturn, recentRoi, tradeCount } = simulationStats ?? {};
    const totalPl = realizedPl == null || unrealizedPl == null ? null : (realizedPl + unrealizedPl) * defaultSets;
    return {
      roi: chartRoi?.value,
      totalPl,
      riskReturn,
      marginRecommended: roundedMarginRecommended?.value == null ? null : roundedMarginRecommended?.value,
      marginRequired: marginRequired?.value == null ? null : marginRequired?.value,
      count: strategyList?.length ?? 0,
      recentRoi,
      tradeCount,
      attribute,
    };
  }, [simulationStats, chartRoi, strategyList, marginRequired, roundedMarginRecommended, defaultSets, attribute]);
  return {
    chartData,
    simulationData,
    serviceId,
  };
};

export const useSimulationChartDataLab = (countValue) => {
  const selectedData = useSelectedDataLab();
  const chartData = useSimulationChartDataLabInternal({
    ...selectedData,
    defaultSets: countValue ?? selectedData.defaultSets,
  });
  return {
    ...chartData,
    loading: selectedData.loading,
    labId: selectedData.labId,
    strategyList: selectedData.strategyList,
    defaultSets: selectedData.defaultSets,
  };
};

export const useSimulationDataLab = () => {
  const selectedData = useSelectedDataLab();
  const tableData = useExecutionTableDataLabInternal(selectedData);
  const chartData = useSimulationChartDataLabInternal(selectedData);
  const { loading, strategyList, labId } = selectedData;
  const strategies = useMemo(() => {
    return (strategyList ?? []).map((strategy) => normalizeLabStrategy(strategy));
  }, [strategyList]);
  return {
    ...tableData,
    ...chartData,
    strategies,
    loading,
    labId,
  };
};

export const useAddToCartPayload = () => {
  const dispatch = useDispatch();
  const isLab = useSelectedShareItem();
  const selectionData = useSelectedDataSelection();
  const labData = useSelectedDataLab();
  const data = isLab ? labData : selectionData;
  const { labId, selectionId, selectionVersion, defaultSets, simulationStats } = data;
  const { marginRecommended, marginRequired, marginRequiredFX, marginRequiredETF, marginRequiredCFD } =
    simulationStats ?? {};
  const addToCart = useCallback(
    (sets) => {
      const payload = isLab ? { labId, labSets: sets } : { selectionId, selectionVersion, selectionSets: sets };
      dispatch(addToCartRequest(payload));
    },
    [dispatch, isLab, labId, selectionId, selectionVersion],
  );
  return useMemo(
    () => ({
      payload: {
        margin: marginRecommended,
        marginRequired,
        marginRequiredFX, // mobile only
        marginRequiredETF, // mobile only
        marginRequiredCFD, // mobile only
        sets: defaultSets,
        isLaunchInsideDetailModal: true,
        callback: addToCart,
        isLab,
      },
    }),
    [
      marginRecommended,
      marginRequired,
      marginRequiredFX,
      marginRequiredETF,
      marginRequiredCFD,
      defaultSets,
      addToCart,
      isLab,
    ],
  );
};

export const useRunNowPayload = ({ openStrategiesSuccessModal }) => {
  const dispatch = useDispatch();
  const isLab = useSelectedShareItem();
  const selectionData = useSelectedDataSelection();
  const labData = useSelectedDataLab();
  const data = isLab ? labData : selectionData;
  const { selectionId, selectionVersion, labId, serviceIds, simulationStats, defaultSets } = data;
  const instrumentList = useSelector((state) => state.settings.instrumentList);
  const strategiesInfo = useMemo(() => {
    if (!data?.strategyList) return null;
    if (isLab) {
      return data.strategyList.map(({ id, instrumentId }) => ({
        strategyId: id,
        serviceId: getServiceId(instrumentId, instrumentList),
      }));
    }
    return data.strategyList.map(({ strategyId, strategyDetail }) => ({
      strategyId,
      serviceId: strategyDetail.serviceId,
    }));
  }, [isLab, data.strategyList, instrumentList]);

  const {
    marginRecommended,
    marginRecommendedFX,
    marginRecommendedETF,
    marginRecommendedCFD,
    marginRequired,
    marginRequiredFX,
    marginRequiredETF,
    marginRequiredCFD,
  } = simulationStats ?? {};

  const callback = useCallback(
    (resultArray) => {
      dispatch(openStrategiesSuccessModal({ resultArray }));
      serviceIds.forEach((sid) => {
        dispatch(getApGroupRequest({ serviceId: sid }));
      });
      dispatch(getTechnicalBuilderDataRequest({ status: CARD_STATUS_IDS[AP_GROUP_STATUSES.ACTIVE] }));
    },
    [dispatch, openStrategiesSuccessModal, serviceIds],
  );

  const runNow = useCallback(
    (sets) => {
      const payload = { sets, callback };
      if (isLab) {
        payload.labId = labId;
      } else {
        payload.selectionId = selectionId;
        payload.selectionVersion = selectionVersion;
      }
      dispatch(saveCreatingStrategiesInfo({ strategies: strategiesInfo }));
      dispatch(createSelectionApGroupRequest(payload));
    },
    [dispatch, isLab, selectionId, selectionVersion, labId, callback, strategiesInfo],
  );

  // lab の場合、service 固定なので marginRecommendedXXX は undefined
  return useMemo(
    () => ({
      payload: {
        marginRecommended,
        marginRecommendedFX: isLab && serviceIds?.includes?.(FX) ? marginRecommended : marginRecommendedFX,
        marginRecommendedETF: isLab && serviceIds?.includes?.(ETF) ? marginRecommended : marginRecommendedETF,
        marginRecommendedCFD: isLab && serviceIds?.includes?.(CFD) ? marginRecommended : marginRecommendedCFD,
        marginRequired,
        marginRequiredFX,
        marginRequiredETF,
        marginRequiredCFD,
        sets: defaultSets,
        isLab,
        callback: runNow,
      },
      serviceIds,
    }),
    [
      isLab,
      marginRecommended,
      marginRecommendedFX,
      marginRecommendedETF,
      marginRecommendedCFD,
      marginRequired,
      marginRequiredFX,
      marginRequiredETF,
      marginRequiredCFD,
      defaultSets,
      runNow,
      serviceIds,
    ],
  );
};

export const useSelectionDetail = () => {
  const [strategyGroup] = useSelectedStrategyGroup();
  const loadingSelections = useLoadingSelections();
  const loadingDetail = useSelector((state) => state.autoSelect.selectionModalInfoIsLoading);
  const loadingDetailTerm = useSelector((state) => state.autoSelect.selectionModalTermInfoIsLoading);
  const loadingSimulationLab = useSelector((state) => state.labs.publishedLabs.simulation.loading);
  const changingSimulationTermLab = useSelector((state) => state.labs.publishedLabs.changingSimulationTerm);
  const { id, loading } = useSelector((state) => state.autoSelect.addToCartRequestInfo);
  const loadingRunNow = useSelector((state) => state.autoSelect.createSelectionApGroupRequestIsLoading);
  const loadingAddToCart = useMemo(() => {
    if (strategyGroup == null) {
      return false;
    }
    const targetId = strategyGroup.labId ?? strategyGroup.selectionId;
    return id === targetId && loading;
  }, [id, loading, strategyGroup]);
  const isSoldOut = useMemo(() => getStockLeft(strategyGroup) <= 0, [strategyGroup]);

  const { strategyList } = useSelectedDataSelection();

  return useMemo(
    () => ({
      strategyGroup,
      loadingSelections,
      loadingAddToCart,
      loadingRunNow,
      loadingDetail,
      loadingDetailTerm,
      loadingDetailLab: loadingSimulationLab && !changingSimulationTermLab,
      isSoldOut,
      strategyList,
    }),
    [
      strategyGroup,
      loadingSelections,
      loadingAddToCart,
      loadingRunNow,
      loadingDetail,
      loadingDetailTerm,
      isSoldOut,
      loadingSimulationLab,
      changingSimulationTermLab,
      strategyList,
    ],
  );
};

export const useAllSelectionTermOptions = () => {
  const termOptions = useSelector((state) => state.constants.selectionTermOptions);
  return useMemo(() => [...termOptions, { label: '開始日', value: TERM_ID_FROM_START_DATE }], [termOptions]);
};

export const checkIsTrendFollow = (strategyList) => {
  return (
    Array.isArray(strategyList) &&
    strategyList.every((s) => s?.strategyDetail?.logic?.logicId === TREND_STRATEGY_LOGIC_ID)
  );
};

const checkLimitOptions = (strategyList) => {
  if (!Array.isArray(strategyList) || strategyList.length === 0) {
    return null;
  }
  const logicIds = strategyList.map((s) => s?.strategyDetail?.logic?.logicId);
  const uniqueLogicIds = new Set(logicIds);
  // logicIDが複数個の場合、表示されるシミュレーション期間を制限しない
  if (uniqueLogicIds.size !== 1) {
    return null;
  }
  const logicId = logicIds[0];
  if (LOGIC_ID_TERM_OPTIONS_MAP.has(logicId)) {
    return new Set(LOGIC_ID_TERM_OPTIONS_MAP.get(logicId));
  }
  return null;
};

export const useSelectionTermOptions = () => {
  const termOptions = useSelector((state) => state.constants.selectionTermOptions);
  const allTermOptions = useAllSelectionTermOptions();
  const selectionModalInfo = useSelector((state) => state.autoSelect.selectionModalInfo);
  // selectionModalInfoはtermIdごとにSelectionDetailを保持しているが、
  // startDateはselectionの情報なのでtermIdによる違いはない
  // いずれかのSelectionDetailでstartDateを取得できていれば、それを使用する（termId切り替えによるちらつき防止）

  const strategyList = allTermOptions
    .map(({ value: termId }) => selectionModalInfo?.[termId]?.strategyList)
    .find((termStrategyList) => termStrategyList);
  const hasStartDate = !!allTermOptions.find(({ value: termId }) => selectionModalInfo?.[termId]?.startDate);
  const baseOptions = hasStartDate ? allTermOptions : termOptions;

  // シミュレーション期間の表示制御
  const displayTermOptions = checkLimitOptions(strategyList);
  return useMemo(
    () => (displayTermOptions ? baseOptions.filter(({ value }) => displayTermOptions.has(value)) : baseOptions),
    [displayTermOptions, baseOptions],
  );
};

export const useLabTermOptions = () => {
  const termOptions = useSelector((state) => state.constants.selectionTermOptions);
  return useMemo(() => [{ label: '稼働日', value: DEFAULT_LAB_SELECTED_TERM_ID }].concat(termOptions), [termOptions]);
};

export const useSelectionTermOptionMap = () => {
  const termOptions = useAllSelectionTermOptions();
  return useMemo(() => new Map(termOptions.map((termOption) => [termOption.value, termOption.label])), [termOptions]);
};

export const useSimulationSummarySelection = () => {
  const loading = useTableLoading();
  const termId = useSelector((state) => state.autoSelect.selectedTermId);
  const { simulationTradesData, autoTradeSettingsData } = useSelectionsTableData(termId);
  return useSimulationSummary({ loading, simulationTradesData, autoTradeSettingsData });
};

export const useSimulationSummaryLab = () => {
  const { simulationTradesData, autoTradeSettingsData, loading } = useExecutionTableDataLab();
  return useSimulationSummary({ loading, simulationTradesData, autoTradeSettingsData });
};

const isEmptyCondition = (state) => {
  if (state == null) {
    return true;
  }
  const keys = Object.keys(state);
  if (keys.length === 0) {
    return true;
  }
  return keys.every((key) => {
    const child = state[key];
    if (child == null) {
      return true;
    }
    const childKeys = Object.keys(child);
    if (childKeys.length === 0) {
      return true;
    }
    return childKeys.every((childKey) => (child[childKey] ?? false) === false);
  });
};

export const useFilterConditionLogic = () => {
  const dispatch = useDispatch();
  const { filterCondition } = useSelector((state) => state.autoSelect);
  const [newFilterCondition, setNewFilterCondition] = useState({ ...filterCondition });
  const [visible, setVisible] = useState(false);

  const nothing = useMemo(() => isEmptyCondition(filterCondition), [filterCondition]);

  const handleOpen = useCallback(() => {
    setNewFilterCondition({ ...filterCondition });
    setVisible(true);
  }, [filterCondition]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setNewFilterCondition({});
  }, []);

  const handleOk = useCallback(() => {
    dispatch(saveAutoSelectFilterCondition({ filterCondition: newFilterCondition }));
    handleClose();
  }, [dispatch, newFilterCondition, handleClose]);

  const handleChange = useCallback(
    (checked, key, item) => {
      setNewFilterCondition({
        ...newFilterCondition,
        [key]: {
          ...(newFilterCondition[key] || {}),
          [item.id]: checked,
        },
      });
    },
    [newFilterCondition],
  );

  const handleClear = useCallback(() => {
    setNewFilterCondition({});
  }, []);

  useEffect(() => {
    setNewFilterCondition({ ...filterCondition });
  }, [filterCondition]);

  return useMemo(() => {
    return {
      visible,
      nothing,
      filterCondition: newFilterCondition,
      handleOpen,
      handleClose,
      handleOk,
      handleChange,
      handleClear,
    };
  }, [visible, nothing, newFilterCondition, handleOpen, handleClose, handleOk, handleChange, handleClear]);
};

export const useFilterConditionModal = ({ filterCondition, onChange }) => {
  const handleChangeRangeOuts = useCallback(
    (checked) => {
      onChange(checked, KEY_RANGE_OUTS, { id: ID_RANGE_OUTS_EXCLUDE_RANGE_OUT });
    },
    [onChange],
  );
  const checkedRangeOuts = filterCondition[KEY_RANGE_OUTS]?.[ID_RANGE_OUTS_EXCLUDE_RANGE_OUT] ?? false;
  return { handleChangeRangeOuts, checkedRangeOuts };
};

export const useGetSelectionInstrumentIds = ({ isShare, strategyList, selectionStrategyDetailList }) => {
  return useMemo(() => {
    const sourceList = isShare ? strategyList : selectionStrategyDetailList;
    return [
      ...new Set(
        sourceList
          ?.map((s) => (isShare ? s.strategyDetail?.instrumentId : s.instrumentId))
          // undefined/null を除外
          .filter(Boolean),
      ),
    ];
  }, [isShare, strategyList, selectionStrategyDetailList]);
};

export const checkIsBuilder = (strategyList) => {
  if (!Array.isArray(strategyList)) return false;

  return strategyList.some((strategy) => {
    const logic = strategy?.strategyDetail?.logic;
    return logic?.logicId === 100;
  });
};
